/**
 * Browser adapter: the DOM canvas behind the same `RenderEnvironment` interface.
 *
 * This is the default render path in production — the user's own GPU does the work, so
 * exports cost the server nothing and come back in milliseconds.
 *
 * `OffscreenCanvas` is preferred when available so the same code can run inside a worker
 * and keep a 500-image batch off the main thread.
 */
import type { Ctx2D, RenderCanvas, RenderEnvironment, RenderImage } from './env';

type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

class BrowserImage implements RenderImage {
  constructor(readonly handle: CanvasImageSource) {}

  get width(): number {
    return (this.handle as HTMLImageElement).naturalWidth || (this.handle as HTMLImageElement).width;
  }

  get height(): number {
    return (this.handle as HTMLImageElement).naturalHeight || (this.handle as HTMLImageElement).height;
  }
}

class BrowserCanvas implements RenderCanvas {
  readonly ctx: Ctx2D;
  /** Set when this surface was put in the document by the adapter, so `release` knows to take it out. */
  private hosted = false;

  constructor(readonly handle: AnyCanvas) {
    const ctx = handle.getContext('2d') as Ctx2D | null;
    if (!ctx) throw new Error('Could not acquire a 2D context.');
    this.ctx = ctx;
  }

  markHosted(): void {
    this.hosted = true;
  }

  release(): void {
    if (!this.hosted) return;
    this.hosted = false;
    const canvas = this.handle as HTMLCanvasElement;
    canvas.remove();
    // Zeroing frees the backing store now rather than when the element is finally collected, and a
    // released surface is never drawn into again.
    canvas.width = 0;
    canvas.height = 0;
  }

  get width(): number {
    return this.handle.width;
  }

  get height(): number {
    return this.handle.height;
  }

  async toPng(): Promise<Uint8Array> {
    // Copied onto a canvas whose context has `alpha: false` first. A browser writes an opaque canvas
    // as colour type 2 and an RGBA one as colour type 6, and App Store Connect refuses an alpha
    // channel even when every pixel is opaque — the same trap the Node adapter hit.
    const target = createSurface(this.handle.width, this.handle.height);
    const targetCtx = target.getContext('2d', { alpha: false }) as Ctx2D | null;
    if (!targetCtx) throw new Error('Could not acquire an opaque 2D context.');
    // Fill first: `alpha: false` starts black, and the source may have transparent corners.
    targetCtx.fillStyle = '#000000';
    targetCtx.fillRect(0, 0, target.width, target.height);
    targetCtx.drawImage(this.handle as CanvasImageSource, 0, 0);
    return encodeCanvasPng(target);
  }

  toRgba(): Uint8Array {
    const { data } = this.ctx.getImageData(0, 0, this.handle.width, this.handle.height);
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
}

/** Encode a canvas as PNG bytes, preferring OffscreenCanvas where it exists. */
async function encodeCanvasPng(canvas: AnyCanvas): Promise<Uint8Array> {
  if (typeof OffscreenCanvas !== 'undefined' && canvas instanceof OffscreenCanvas) {
    const blob = await canvas.convertToBlob({ type: 'image/png' });
    return new Uint8Array(await blob.arrayBuffer());
  }
  const blob = await new Promise<Blob | null>((resolve) =>
    (canvas as HTMLCanvasElement).toBlob(resolve, 'image/png'),
  );
  if (!blob) throw new Error('toBlob returned null.');
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * A DOM canvas wherever there is a document, even when nobody is going to append it.
 *
 * `OffscreenCanvas` rasterises text slightly differently — subpixel antialiasing is available to a
 * canvas on the page and not to one that is not — and the editor has to draw into a DOM canvas because
 * its canvas *is* the page. Rendering the export into an OffscreenCanvas therefore made every glyph of
 * every caption differ by up to 208 in a channel: 11,774 pixels of a panel whose whole promise is that
 * the download is the preview. The same surface type on both sides is what keeps that promise, and the
 * engine's choice of rasteriser is not a decision this product gets to leave to chance.
 */
function createSurface(width: number, height: number): AnyCanvas {
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    surfaceHost()?.append(canvas);
    return canvas;
  }
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  throw new Error('No browser canvas available: neither document nor OffscreenCanvas exists.');
}

/**
 * Where the adapter's surfaces live.
 *
 * The same text rasterises differently in a canvas that is in the document and one that is not:
 * measured on a single caption, 6,282 channel values differed with a worst delta of 217 — subpixel
 * antialiasing is available only to a canvas the compositor knows about. The editor's canvas is
 * necessarily in the page, because it *is* the page, so the surfaces the export renders into have to be
 * as well; otherwise "the download is the preview" is false by a few thousand pixels per caption.
 *
 * Hidden by being pushed off-screen rather than by `display`, `visibility` or `opacity` — none of which
 * the compositor treats the same way. A canvas in this host measures identical to one in the body, and
 * one that is not in the document at all does not.
 */
function surfaceHost(): HTMLElement | null {
  if (typeof document === 'undefined' || !document.body) return null;
  const existing = document.getElementById('keymaker-surfaces');
  if (existing) return existing;
  const host = document.createElement('div');
  host.id = 'keymaker-surfaces';
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    'position:fixed;left:-10000px;top:0;width:0;height:0;overflow:hidden;pointer-events:none';
  document.body.append(host);
  return host;
}

export interface BrowserEnvironmentOptions {
  /**
   * Resolve a scene's asset key to a URL. The caller owns this because a Node render reads
   * from disk while a browser render reads from an object URL or a signed CDN link.
   */
  resolveUrl?: (src: string) => string;
}

let cachedCanvas: AnyCanvas | null = null;

export function createBrowserEnvironment(options: BrowserEnvironmentOptions = {}): RenderEnvironment {
  const resolveUrl = options.resolveUrl ?? ((src: string) => src);

  return {
    createCanvas(width, height) {
      const canvas = new BrowserCanvas(createSurface(width, height));
      // Only the surfaces this adapter put in the document are its to take out again.
      if ((canvas.handle as HTMLCanvasElement).parentElement?.id === 'keymaker-surfaces') canvas.markHosted();
      return canvas;
    },

    async loadImage(src) {
      const url = resolveUrl(src);
      const image = new Image();
      image.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        image.src = url;
      });
      return new BrowserImage(image);
    },

    registerFont(family, bytes) {
      if (typeof document === 'undefined' || typeof FontFace === 'undefined') return;
      // Copy into a standalone buffer: a Uint8Array view over a larger ArrayBuffer would
      // otherwise ship the whole backing store to the FontFace.
      const face = new FontFace(family, bytes.slice().buffer as ArrayBuffer);
      void face.load().then((loaded) => {
        (document.fonts as FontFaceSet).add(loaded);
      });
    },

    /**
     * Tier 3, loaded on demand.
     *
     * The `import()` is what keeps Three.js — around 600 KB — out of the bundle for every project
     * that only uses Tier 1 and Tier 2, which is most of them. A bundler splits it into a chunk
     * fetched the first time a scene actually contains a `kind: "3d"` device.
     */
    async render3d(request) {
      const { renderDevice } = await import('./three-d');
      const rendered = await renderDevice({
        ...(request.key ? { key: request.key } : {}),
        frame: request.frame,
        screen: request.screen.handle as TexImageSource,
        width: request.width,
        height: request.height,
        // Spread rather than assign: `exactOptionalPropertyTypes` distinguishes an absent key from
        // one explicitly set to undefined, and the 3D request treats them differently.
        ...(request.styling ? { styling: request.styling } : {}),
        ...(request.rotation ? { rotation: request.rotation } : {}),
        ...(request.zoom !== undefined ? { zoom: request.zoom } : {}),
      });
      return new BrowserImage(rendered.handle as CanvasImageSource);
    },

    drawImage(ctx, image, ...args) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ctx as any).drawImage(image.handle, ...args);
    },
  };
}

/** The shared measurement surface, so text layout does not allocate per call. */
export function measurementSurface(width = 8, height = 8): Ctx2D {
  if (cachedCanvas === null) cachedCanvas = createSurface(width, height);
  const ctx = cachedCanvas.getContext('2d') as Ctx2D | null;
  if (!ctx) throw new Error('Could not acquire a 2D context for measurement.');
  return ctx;
}
